import type { IAccount } from 'src/types/login';

export interface AccountTokensResult {
    available: IAccount[];
    unavailable: IAccount[];
}

/**
 * Sends the session token to every company the user belongs to, in parallel.
 * A company whose request fails (network error, HTTP error) is reported as unavailable
 * instead of failing the whole login: the other companies keep working.
 * A request that reaches the server counts as success whatever its body, as before.
 */
export const updateAccountTokens = async (
    accounts: IAccount[],
    update: (account: IAccount) => Promise<unknown>
): Promise<AccountTokensResult> => {
    const succeeded = await Promise.all(
        accounts.map((account) =>
            Promise.resolve()
                .then(() => update(account))
                .then(
                    () => true,
                    () => false
                )
        )
    );

    return {
        available: accounts.filter((_, i) => succeeded[i]),
        unavailable: accounts.filter((_, i) => !succeeded[i]),
    };
};

export const unavailableAccountNames = (accounts: IAccount[]): string =>
    accounts.map((account) => account.accountName || String(account.accountCode)).join(', ');

/** "Company X is not available at the moment..." for one or several companies; empty when all are available. */
export const unavailableAccountsMessage = (
    accounts: IAccount[],
    t: (key: string, options?: Record<string, unknown>) => string
): string => {
    if (!accounts.length) return '';
    const key = accounts.length === 1 ? 'companyUnavailable' : 'companiesUnavailable';
    return t(key, { names: unavailableAccountNames(accounts) });
};

/** The company to work on after some were dropped: the current one if it is still there, else the first. */
export const pickSelectedAccount = (accounts: IAccount[], current?: IAccount | null): IAccount | null =>
    accounts.find((account) => account.contactUUID === current?.contactUUID) || accounts[0] || null;
